import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import InfoTooltip from "../components/common/InfoTooltip";
import ModalCloseX from "../components/common/ModalCloseX";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import {
  getAppointmentNotificationSettings,
  previewAppointmentNotification,
  updateAppointmentNotificationSettings,
} from "../api/appointmentNotifications";
import { getCurrentSalonId, getCurrentSalonName } from "../utils/currentSalon";
import SafeHtml from "../components/common/SafeHtml";
import "./OnlineBookingSectionCompact.css";

const TOOLTIP_TEXT = {
  confirm_toggle: "Turn confirmation emails on or off for new appointments.",
  reminder_toggle: "Turn reminder emails on or off for upcoming appointments.",

  no_show_toggle: "Turn no-show follow-up emails on or off when staff mark a cancelled appointment as a no-show.",
  reminder_timing:
    "Reminder timing uses your salon's local timezone for scheduling.",
  subject:
    "Subjects can include placeholders like {{client_first_name}} or {{appointment_date}}.",
  body:
    "Message bodies support placeholders. Use the helper list to personalize emails.",
  preview: "Preview uses sample values in the salon timezone.",
};

const PLACEHOLDERS = [
  { token: "{{client_first_name}}", label: "Client first name" },
  { token: "{{salon_name}}", label: "Salon name" },
  { token: "{{appointment_date}}", label: "Appointment date (salon time)" },
  { token: "{{appointment_time}}", label: "Appointment time (salon time)" },
  { token: "{{appointment_datetime}}", label: "Appointment date & time (salon time)" },
  { token: "{{technician_name}}", label: "Technician name" },
  { token: "{{service_summary}}", label: "Service summary" },
  { token: "{{salon_phone}}", label: "Salon phone" },
  { token: "{{salon_address}}", label: "Salon address" },
  { token: "{{portal_link}}", label: "Client portal link (if available)" },
  { token: "{{cancel_reschedule_link}}", label: "Cancel/reschedule link (if available)" },
];
const MANAGE_LINK_TOKEN = "{{cancel_reschedule_link}}";
const PREVIEW_FROM_EMAIL = "no-reply@ellyanna.com";

const defaultPreviewState = {
  subject: "",
  body: "",
  body_html: "",
};

const HELP_SECTIONS = [
  {
    id: "email-overview",
    title: "What this page does",
    description: "Configure confirmation/reminder templates and reminder timing.",
    anchorId: "aes-overview",
    bullets: [
      "Enable or disable confirmation and reminder emails.",
      "Set reminder lead time in hours or days.",
      "Preview both templates before saving.",
    ],
    tips: [
      "Keep subject lines clear and short for better open rates.",
      "Use placeholders to personalize messages without editing each email.",
    ],
  },
  {
    id: "confirm-template",
    title: "Confirmation email setup",
    description: "Adjust confirmation subject, body, placeholders, and preview.",
    anchorId: "aes-confirmation",
  },
  {
    id: "reminder-template",
    title: "Reminder email setup",
    description: "Set timing plus reminder subject/body and preview.",
    anchorId: "aes-reminder",
  },
  {
    id: "no-show-template",

    title: "No-show follow-up email",

    description: "Adjust the email sent when staff mark a cancelled appointment as a no-show.",

    anchorId: "aes-no-show",

  },

  {

    id: "placeholder-reference",
    title: "Placeholder reference",
    description: "Review available tokens and quick timezone reminder.",
    anchorId: "aes-placeholders",
  },
];

const normalizeSettings = (data) => ({
  email_confirm_enabled: Boolean(data?.email_confirm_enabled ?? true),
  email_confirm_subject: data?.email_confirm_subject || "",
  email_confirm_body: data?.email_confirm_body || "",
  email_reminder_enabled: Boolean(data?.email_reminder_enabled ?? true),
  email_reminder_subject: data?.email_reminder_subject || "",
  email_reminder_body: data?.email_reminder_body || "",
  email_no_show_followup_enabled: Boolean(data?.email_no_show_followup_enabled ?? true),

  email_no_show_followup_subject: data?.email_no_show_followup_subject || "",

  email_no_show_followup_body: data?.email_no_show_followup_body || "",

  reminder_timing_mode: data?.reminder_timing_mode || "hours",
  reminder_lead_time: String(data?.reminder_lead_time ?? 24),
});

export default function AppointmentEmailSettings() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const salonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState(normalizeSettings(null));
  const [initialForm, setInitialForm] = useState(null);
  const [preview, setPreview] = useState({
    confirmation: defaultPreviewState,
    reminder: defaultPreviewState,

    no_show_followup: defaultPreviewState,
  });
  const [previewLoading, setPreviewLoading] = useState({
    confirmation: false,
    reminder: false,

    no_show_followup: false,
  });
  const [previewError, setPreviewError] = useState({
    confirmation: "",
    reminder: "",

    no_show_followup: "",
  });
  const [highlightedSectionId, setHighlightedSectionId] = useState("");
  const highlightTimeoutRef = useRef(null);

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    },
    []
  );

  const hasChanges = useMemo(() => {
    if (!initialForm) return false;
    return JSON.stringify(form) !== JSON.stringify(initialForm);
  }, [form, initialForm]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getAppointmentNotificationSettings(token);
      const normalized = normalizeSettings(data);
      setForm(normalized);
      setInitialForm(normalized);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Unable to load appointment email settings."
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleFieldChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const insertManageLink = (field) => {
    setForm((prev) => {
      const current = prev[field] || "";
      if (current.includes(MANAGE_LINK_TOKEN)) {
        return prev;
      }
      const next = current ? `${current}\n${MANAGE_LINK_TOKEN}` : MANAGE_LINK_TOKEN;
      return { ...prev, [field]: next };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    const payload = {
      email_confirm_enabled: Boolean(form.email_confirm_enabled),
      email_confirm_subject: form.email_confirm_subject,
      email_confirm_body: form.email_confirm_body,
      email_reminder_enabled: Boolean(form.email_reminder_enabled),
      email_reminder_subject: form.email_reminder_subject,
      email_reminder_body: form.email_reminder_body,
      email_no_show_followup_enabled: Boolean(form.email_no_show_followup_enabled),

      email_no_show_followup_subject: form.email_no_show_followup_subject,

      email_no_show_followup_body: form.email_no_show_followup_body,

      reminder_timing_mode: form.reminder_timing_mode,
      reminder_lead_time: Number(form.reminder_lead_time || 0),
    };
    try {
      const data = await updateAppointmentNotificationSettings(payload, token);
      const normalized = normalizeSettings(data);
      setForm(normalized);
      setInitialForm(normalized);
      setNotice("Appointment email settings saved.");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Unable to save appointment email settings."
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (type) => {
    setPreviewLoading((prev) => ({ ...prev, [type]: true }));
    setPreviewError((prev) => ({ ...prev, [type]: "" }));
    try {
      const subjectByType = {
        confirmation: form.email_confirm_subject,
        reminder: form.email_reminder_subject,
        no_show_followup: form.email_no_show_followup_subject,
      };

      const bodyByType = {
        confirmation: form.email_confirm_body,
        reminder: form.email_reminder_body,
        no_show_followup: form.email_no_show_followup_body,
      };

      const payload = {
        type,
        template_subject: subjectByType[type] || "",
        template_body: bodyByType[type] || "",
      };
      const data = await previewAppointmentNotification(payload, token);
      setPreview((prev) => ({
        ...prev,
        [type]: {
          subject: data?.subject || "",
          body: data?.body || "",
          body_html: data?.body_html || "",
        },
      }));
    } catch (err) {
      setPreviewError((prev) => ({
        ...prev,
        [type]:
          err?.response?.data?.detail || "Unable to generate preview. Please try again.",
      }));
    } finally {
      setPreviewLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  const jumpToSectionWithHighlight = useCallback((anchorId) => {
    if (!anchorId) return;
    const element = document.getElementById(anchorId);
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlightedSectionId(anchorId);
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedSectionId("");
    }, 1800);
  }, []);

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="onlineBookingShell">
      <div className="onlineBookingWide online-booking-medium online-booking-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 relative">
            <div className="flex flex-wrap items-center justify-between gap-3 pr-12">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-white">
                  Appointment Confirmations &amp; Reminders
                </h1>
                <p className="text-sm md:text-base text-rose-100">
                  Customize what clients receive and when reminders are sent.
                </p>
              </div>
              <button
                type="button"
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="rounded-full bg-white/95 px-4 py-2 text-xs md:text-sm font-semibold text-rose-600 hover:bg-white disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save Settings"}
              </button>
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            {salonName && (
              <div className="text-xs text-slate-500">
                {salonName}
                {salonId ? ` - Salon ID ${salonId}` : ""}
              </div>
            )}
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            )}
            {notice && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                {notice}
              </div>
            )}

            <div id="aes-overview">
              <ReportHelpCard
                title="Help Guide: Appointment Confirmations & Reminders"
                subtitle="Set up confirmations, reminders, placeholders, and preview flow."
                sections={HELP_SECTIONS}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            {loading ? (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-600">
                Loading appointment email settings...
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                <div className="space-y-6">
                  <div
                    id="aes-confirmation"
                    className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-5 transition ${
                      highlightedSectionId === "aes-confirmation"
                        ? "ring-2 ring-rose-300"
                        : ""
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                          Appointment Confirmation Email
                        </h2>
                        <p className="text-xs text-slate-500">
                          Sent immediately after a client books or is booked.
                        </p>
                      </div>
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={Boolean(form.email_confirm_enabled)}
                          onChange={(e) =>
                            handleFieldChange("email_confirm_enabled", e.target.checked)
                          }
                          className="h-4 w-4 rounded border-slate-300 text-slate-900"
                        />
                        <span className="inline-flex items-center gap-2">
                          Send confirmation email
                          <InfoTooltip
                            text={TOOLTIP_TEXT.confirm_toggle}
                            label="Send confirmation email"
                          />
                        </span>
                      </label>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                            Subject
                            <InfoTooltip text={TOOLTIP_TEXT.subject} label="Subject" />
                          </label>
                          <input
                            type="text"
                            value={form.email_confirm_subject}
                            onChange={(e) =>
                              handleFieldChange("email_confirm_subject", e.target.value)
                            }
                            className="h-11 w-full rounded-xl border border-slate-300 px-4"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                            Message
                            <InfoTooltip text={TOOLTIP_TEXT.body} label="Message" />
                          </label>
                          <textarea
                            rows={6}
                            value={form.email_confirm_body}
                            onChange={(e) =>
                              handleFieldChange("email_confirm_body", e.target.value)
                            }
                            className="w-full rounded-2xl border border-slate-300 px-4 py-3"
                          />
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => insertManageLink("email_confirm_body")}
                              className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              Insert manage link
                            </button>
                            <span className="text-xs text-slate-500">
                              Adds the cancel/reschedule link placeholder.
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handlePreview("confirmation")}
                          className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          disabled={previewLoading.confirmation}
                        >
                          {previewLoading.confirmation
                            ? "Generating..."
                            : "Preview email"}
                        </button>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                            Email preview
                            <InfoTooltip text={TOOLTIP_TEXT.preview} label="Preview" />
                          </div>
                          <div className="text-xs text-slate-500">
                            Uses sample values.
                          </div>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
                          <div>
                            <span className="font-semibold text-slate-700">From:</span>{" "}
                            {(salonName || "Your Salon") + ` via Ellyanna <${PREVIEW_FROM_EMAIL}>`}
                          </div>
                          <div>
                            <span className="font-semibold text-slate-700">To:</span> client@example.com
                          </div>
                          <div>
                            <span className="font-semibold text-slate-700">Subject:</span>{" "}
                            {preview.confirmation.subject || "No subject"}
                          </div>
                        </div>
                        {previewError.confirmation ? (
                          <div className="text-xs text-rose-600">
                            {previewError.confirmation}
                          </div>
                        ) : preview.confirmation.body_html ? (
                          <SafeHtml
                            className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 whitespace-pre-wrap"
                             html={preview.confirmation.body_html} />
                        ) : (
                          <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-6 text-sm text-slate-400 text-center">
                            Preview will appear here.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div
                    id="aes-reminder"
                    className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-5 transition ${
                      highlightedSectionId === "aes-reminder" ? "ring-2 ring-rose-300" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                          Appointment Reminder Email
                        </h2>
                        <p className="text-xs text-slate-500">
                          Sent automatically before an appointment starts.
                        </p>
                      </div>
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={Boolean(form.email_reminder_enabled)}
                          onChange={(e) =>
                            handleFieldChange("email_reminder_enabled", e.target.checked)
                          }
                          className="h-4 w-4 rounded border-slate-300 text-slate-900"
                        />
                        <span className="inline-flex items-center gap-2">
                          Send reminder email
                          <InfoTooltip
                            text={TOOLTIP_TEXT.reminder_toggle}
                            label="Send reminder email"
                          />
                        </span>
                      </label>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                          Send reminder
                          <InfoTooltip
                            text={TOOLTIP_TEXT.reminder_timing}
                            label="Reminder timing"
                          />
                        </label>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            value={form.reminder_lead_time}
                            onChange={(e) =>
                              handleFieldChange(
                                "reminder_lead_time",
                                e.target.value
                              )
                            }
                            className="h-10 w-24 rounded-xl border border-slate-300 px-3"
                          />
                          <select
                            value={form.reminder_timing_mode}
                            onChange={(e) =>
                              handleFieldChange("reminder_timing_mode", e.target.value)
                            }
                            className="h-10 rounded-xl border border-slate-300 px-3"
                          >
                            <option value="hours">hours</option>
                            <option value="days">days</option>
                          </select>
                          <span className="text-sm text-slate-500">before appointment</span>
                        </div>
                        <div className="text-xs text-slate-500">
                          Uses salon timezone for timing.
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                            Subject
                            <InfoTooltip text={TOOLTIP_TEXT.subject} label="Subject" />
                          </label>
                          <input
                            type="text"
                            value={form.email_reminder_subject}
                            onChange={(e) =>
                              handleFieldChange(
                                "email_reminder_subject",
                                e.target.value
                              )
                            }
                            className="h-11 w-full rounded-xl border border-slate-300 px-4"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                            Message
                            <InfoTooltip text={TOOLTIP_TEXT.body} label="Message" />
                          </label>
                          <textarea
                            rows={6}
                            value={form.email_reminder_body}
                            onChange={(e) =>
                              handleFieldChange("email_reminder_body", e.target.value)
                            }
                            className="w-full rounded-2xl border border-slate-300 px-4 py-3"
                          />
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => insertManageLink("email_reminder_body")}
                              className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                            >
                              Insert manage link
                            </button>
                            <span className="text-xs text-slate-500">
                              Adds the cancel/reschedule link placeholder.
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handlePreview("reminder")}
                          className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          disabled={previewLoading.reminder}
                        >
                          {previewLoading.reminder ? "Generating..." : "Preview email"}
                        </button>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">
                            Email preview
                            <InfoTooltip text={TOOLTIP_TEXT.preview} label="Preview" />
                          </div>
                          <div className="text-xs text-slate-500">
                            Uses sample values.
                          </div>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
                          <div>
                            <span className="font-semibold text-slate-700">From:</span>{" "}
                            {(salonName || "Your Salon") + ` via Ellyanna <${PREVIEW_FROM_EMAIL}>`}
                          </div>
                          <div>
                            <span className="font-semibold text-slate-700">To:</span> client@example.com
                          </div>
                          <div>
                            <span className="font-semibold text-slate-700">Subject:</span>{" "}
                            {preview.reminder.subject || "No subject"}
                          </div>
                        </div>
                        {previewError.reminder ? (
                          <div className="text-xs text-rose-600">
                            {previewError.reminder}
                          </div>
                        ) : preview.reminder.body_html ? (
                          <SafeHtml
                            className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 whitespace-pre-wrap"
                             html={preview.reminder.body_html} />
                        ) : (
                          <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-6 text-sm text-slate-400 text-center">
                            Preview will appear here.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div

                    id="aes-no-show"

                    className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-5 transition ${

                      highlightedSectionId === "aes-no-show" ? "ring-2 ring-rose-300" : ""

                    }`}

                  >

                    <div className="flex items-center justify-between">

                      <div>

                        <h2 className="text-lg md:text-xl font-semibold text-slate-900">

                          No-Show Follow-up Email

                        </h2>

                        <p className="text-xs text-slate-500">

                          Sent when staff cancel an appointment and mark it as a no-show.

                        </p>

                      </div>

                      <label className="flex items-center gap-2 text-sm text-slate-700">

                        <input

                          type="checkbox"

                          checked={Boolean(form.email_no_show_followup_enabled)}

                          onChange={(e) =>

                            handleFieldChange("email_no_show_followup_enabled", e.target.checked)

                          }

                          className="h-4 w-4 rounded border-slate-300 text-slate-900"

                        />

                        <span className="inline-flex items-center gap-2">

                          Send no-show follow-up email

                          <InfoTooltip

                            text={TOOLTIP_TEXT.no_show_toggle}

                            label="Send no-show follow-up email"

                          />

                        </span>

                      </label>

                    </div>



                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6">

                      <div className="space-y-4">

                        <div className="space-y-2">

                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">

                            Subject

                            <InfoTooltip text={TOOLTIP_TEXT.subject} label="Subject" />

                          </label>

                          <input

                            type="text"

                            value={form.email_no_show_followup_subject}

                            onChange={(e) =>

                              handleFieldChange("email_no_show_followup_subject", e.target.value)

                            }

                            className="h-11 w-full rounded-xl border border-slate-300 px-4"

                          />

                        </div>

                        <div className="space-y-2">

                          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">

                            Message

                            <InfoTooltip text={TOOLTIP_TEXT.body} label="Message" />

                          </label>

                          <textarea

                            rows={6}

                            value={form.email_no_show_followup_body}

                            onChange={(e) =>

                              handleFieldChange("email_no_show_followup_body", e.target.value)

                            }

                            className="w-full rounded-2xl border border-slate-300 px-4 py-3"

                          />

                          <div className="text-xs text-slate-500">

                            Keep this message polite and practical. The appointment is already cancelled, so no manage link is added.

                          </div>

                        </div>

                        <button

                          type="button"

                          onClick={() => handlePreview("no_show_followup")}

                          className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"

                          disabled={previewLoading.no_show_followup}

                        >

                          {previewLoading.no_show_followup ? "Generating..." : "Preview email"}

                        </button>

                      </div>



                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">

                        <div className="flex items-center justify-between gap-2">

                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-2">

                            Email preview

                            <InfoTooltip text={TOOLTIP_TEXT.preview} label="Preview" />

                          </div>

                          <div className="text-xs text-slate-500">

                            Uses sample values.

                          </div>

                        </div>

                        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">

                          <div>

                            <span className="font-semibold text-slate-700">From:</span>{" "}

                            {(salonName || "Your Salon") + ` via Ellyanna <${PREVIEW_FROM_EMAIL}>`}
                          </div>

                          <div>

                            <span className="font-semibold text-slate-700">To:</span> client@example.com

                          </div>

                          <div>

                            <span className="font-semibold text-slate-700">Subject:</span>{" "}

                            {preview.no_show_followup.subject || "No subject"}

                          </div>

                        </div>

                        {previewError.no_show_followup ? (

                          <div className="text-xs text-rose-600">

                            {previewError.no_show_followup}

                          </div>

                        ) : preview.no_show_followup.body_html ? (

                          <SafeHtml

                            className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 whitespace-pre-wrap"

                             html={preview.no_show_followup.body_html} />

                        ) : (

                          <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-6 text-sm text-slate-400 text-center">

                            Preview will appear here.

                          </div>

                        )}

                      </div>

                    </div>

                  </div>

                </div>

                <div className="space-y-6">
                  <div
                    id="aes-placeholders"
                    className={`rounded-2xl border border-slate-200 bg-white p-5 transition ${
                      highlightedSectionId === "aes-placeholders"
                        ? "ring-2 ring-rose-300"
                        : ""
                    }`}
                  >
                    <div className="text-sm font-semibold text-slate-900">
                      Available placeholders
                    </div>
                    <div className="mt-3 space-y-2 text-sm text-slate-600">
                      {PLACEHOLDERS.map((item) => (
                        <div
                          key={item.token}
                          className="flex items-start justify-between gap-3"
                        >
                          <span className="font-mono text-xs text-slate-700">
                            {item.token}
                          </span>
                          <span className="text-xs text-slate-500">
                            {item.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-xs text-slate-600">
                    Tip: appointment date and time placeholders always display in the
                    salon's local timezone.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
